<template>
  <div class="sticky">
    <div ref="menu-container">
      <template v-if="!this.$store.state.user">
        <pre-paywall-recruiter-menu-items v-if="this.$store.state.guestCurrentRole === guestUserRoles.ROLE_RECRUITER" />
        <pre-paywall-menu-items v-else />
      </template>
      <template v-else>
        <user-nav-menu-items/>
      </template>
    </div>
    <b-container fluid class="position-relative breadcrumbs-container">
      <div class="d-flex justify-content-end align-items-center">
        <div class="my-n1 h-100 pr-3" style="right: 0;">
          <i
              v-if="isInfoIconVisible"
              class="mdi mdi-information-outline clickable"
              aria-hidden="true"
              v-b-tooltip.hover
              title="Show information about this page"
              @click="showInfoPanelByButton"
          />
        </div>
      </div>
    </b-container>
    <b-modal
      size="xl"
      class="text-left job-offer-modal"
      centered
      footer-class="justify-content-center"
      :visible="isIcoTooltipModalVisible"
      hide-footer
      hide-header-close
    >
      <template slot="modal-header">
        <div class="w-100">
          <h5
            class="modal-title text-center font_size_1125rem font-weight-bold v3_color_teal mt-2"
          >
          <span>Welcome to Phairify’s riskless ‘Window-Shopping’</span>
          </h5>
        </div>
      </template>
      <div class="p-3 v3_color_teal text-left">
          <p>Explore our candidate pool at no cost to gauge interest in your positions and engage only the prospects you need precisely when you need them.</p>
          <div
              class="text-left v3_color_teal d-flex flex-column justify-content-center w-100"
            >
            <span>
              1. Select and define required Job Criteria to build your job.
            </span>
            <span>
              2. Assess “Number of Matches” to see the number of candidates interested in your opportunity.
            </span>
            <span>
              3. Click “Connect Your Opportunity” to inform matching candidates their dream job awaits.
            </span>
          </div>
      </div>
      <div class="d-flex align-items-center justify-content-center">
        <b-button
          variant="v2_primary"
          class="p-4 text-center"
          size="sm"
          @click="onClose"
          v-b-tooltip.hover
        >
          Close
        </b-button>
    </div>
    </b-modal>
    <portal to="info-popups">
      <InfoPopup
          :key="$route.path"
          v-if="showDashboardInfoPanel"
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"
          :with-cancel-possibility="isInfoPanelWithCancel"
          setting-key="DASHBOARD"
      >
        <div slot="title" class="v2_info-popup__title px-2">
          Using the Dashboard
        </div>
        <DashboardInfoPopupContent/>
      </InfoPopup>

      <InfoPopup
          :key="$route.path"
          v-else-if="$route.path.startsWith('/user/filters')"
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"
          :with-cancel-possibility="isInfoPanelWithCancel"
          setting-key="FILTERS"
      >
        <div slot="title" class="v2_info-popup__title">
          Using Inform
        </div>
        <FiltersInfoPopup/>
      </InfoPopup>

      <InfoPopup
          :key="$route.path"
          v-else-if="$route.path === '/user/surveyHome'"
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"    
          :with-cancel-possibility="isInfoPanelWithCancel"
          setting-key="STUDIES"
      >
        <div slot="title" class="v2_info-popup__title">
          Using Share
        </div>
        <StudyInfoPopupContent/>
      </InfoPopup>
      <InfoPopup
          v-else-if="$route.path.startsWith('/user/surveyForm')"
          :key="$route.path"
          auto-show-only-once
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"        
          :setting-key="`STUDY_FORM_${($route.params || {}).surveyId}`"
          :content-route="`study-first-time&surveyId=${
          ($route.params || {}).surveyId
        }`"
      >
        <StudyFormHelpPopup
            slot-scope="{ content }"
            :content="content"
            :is-first-render="isFirstRender"
        />
        <div slot="footer">
          <div
              v-if="isFirstRender"
              class="d-flex align-items-center justify-content-center v2_info-popup__footer"
          >
            You can view this at any time by clicking the
            <i class="mdi mdi-information-outline pl-1"/>
          </div>
        </div>
      </InfoPopup>

      <InfoPopup
          :key="$route.path"
          v-else-if="$route.path === '/user/exchange'"
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"
          :with-cancel-possibility="isInfoPanelWithCancel"
          setting-key="EXCHANGE_DASHBOARD"
          class="using-connect-modal"
      >
        <div slot="title" class="v2_info-popup__title">
          Using Connect
        </div>
        <exchange-info-popup/>
      </InfoPopup>

      <InfoPopup
          :key="$route.path"
          v-else-if="$route.path.startsWith('/user/exchange/job-profile')"
          v-model="isInfoPanelVisible"
          :user-settings-key="userSettingsKey"
          :with-cancel-possibility="isInfoPanelWithCancel"
          setting-key="JOB_PROFILE"
      >
        <div slot="title" class="v2_info-popup__title">
          Using ICO Manager
        </div>
        <job-profile-info-popup/>
      </InfoPopup>

      <!--  Successful Registration Modal  -->
      <welcome-modal
          v-if="isWelcomeModalVisible"
          :visible="isWelcomeModalVisible"
          :loading="isWelcomeLoading"
          @close="onCloseWelcomeModal"
          @getStarted="onGetStarted"
      />
    </portal>
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import WelcomeModal from '@/components/auth/WelcomeModal';
import InfoPopupParentMixin from '@/mixins/InfoPopupParentMixin';
import InfoPopup from '@/components/ui-elements/InfoPopup';
import DashboardInfoPopupContent from '@/components/dashboard/DashboardInfoPopupContent';
import FiltersInfoPopup from '@/components/survey/FiltersInfoPopup';
import StudyInfoPopupContent from '@/components/study/StudyInfoPopupContent';
import StudyFormHelpPopup from '@/components/study/form/HelpPopup';
import ExchangeInfoPopup from '@/components/exchange/ExchangeInfoPopup';
import JobProfileInfoPopup from '@/components/exchange/job-profile-details/JobProfileInfoPopup';
import UserNavMenuItems from '@/components/layouts/user/UserNavMenuItems';
import {GUEST_USER_ROLES} from "@/store";
import PrePaywallRecruiterMenuItems from "@/components/layouts/pre-paywall/PrePaywallRecruiterMenuItems";
import PrePaywallMenuItems from "@/components/layouts/pre-paywall/PrePaywallMenuItems";

export default {
  name: 'UserLayout',
  components: {
    PrePaywallRecruiterMenuItems,
    WelcomeModal,
    InfoPopup,
    DashboardInfoPopupContent,
    FiltersInfoPopup,
    StudyInfoPopupContent,
    StudyFormHelpPopup,
    ExchangeInfoPopup,
    JobProfileInfoPopup,
    UserNavMenuItems,
    PrePaywallMenuItems,
  },
  mixins: [InfoPopupParentMixin],
  props: {
    initialReferModalVisible: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      isWelcomeModalVisible: false,
      isWelcomeLoading: false,
      addClassSticky: false,
      guestUserRoles: GUEST_USER_ROLES,
      isIcoTooltipModalVisible: false
    };
  },
  created: function () {
    this.isWelcomeModalVisible = !!this.$route.query.successfulRegistration;
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  computed: {
    user: function () {
      return this.$store.state.user;
    },
    userEmail: function () {
      return this.user ? this.user.email : '';
    },
    userName: function () {
      return this.user
          ? `${this.user.first_name ? this.user.first_name : ''} ${
              this.user.last_name ? this.user.last_name : ''
          }`
          : '';
    },
    showAdminMenu: function () {
      return this.$route.path.indexOf('/admin') === 0;
    },
    currentYear: function () {
      return new Date().getFullYear();
    },
    userSocietyObject: function () {
      return this.user && this.user.society ? this.user.society : null;
    },
    userSocietyName: function () {
      return this.userSocietyObject ? this.userSocietyObject.name : '';
    },
    showDashboardInfoPanel: function () {
      return (
          this.$route.path === '/user/dashboard' &&
          !this.$route.query.hasOwnProperty('refer-a-colleague') &&
          !this.isWelcomeModalVisible
      );
    },
    isSignInVisible: function () {
      return (
          !this.$store.getters.isAuthenticated &&
          this.$route.path.replace(/\/$/, '') === '/help'
      );
    },
    societyLogo: function () {
      return this.userSocietyObject && this.userSocietyObject.logo
          ? this.userSocietyObject.logo
          : null;
    },
  },
  methods: {
    handleScroll: function () {
      let scrollHeight = this.$refs["menu-container"].scrollHeight;
      let scrollTop = window.pageYOffset;
      this.addClassSticky = scrollTop > scrollHeight;
    },
    onGetStarted: function () {
      this.isWelcomeModalVisible = false;
    },
    onCloseWelcomeModal: function () {
      this.isWelcomeModalVisible = false;
    },
    showInfoPanelByButton: function () {
      //this.isIcoTooltipModalVisible = true;
      this.isInfoPanelVisible = true;
      this.isInfoPanelWithCancel = false;
      this.isFirstRender = false;
    },
    onClose: function () {
      this.isIcoTooltipModalVisible = false;
    },
  },
  watch: {
    $route: function () {
      this.isWelcomeModalVisible = !!this.$route.query.successfulRegistration;
    },
  },
};
</script>
<style>
.using-connect-modal .modal {
  padding-right: 15px !important;
}
</style>


<style scoped lang="scss">

.sticky {
  position: fixed !important;
  top: 56px;
  width: 100%;
  z-index: 100;
  background-color: transparent;
}

.society-title-wrapper {
  margin-right: 50px;
}

.breadcrumbs-container {
  margin-top: 10px;
  margin-bottom: 15px;
}
</style>
